import { useDependencies } from "./useDependencies";
import { useAuth } from "./useAuth";
import { useQuery } from "@tanstack/react-query";
import { HTTPError } from "src/contexts/_shared/domain/errors/HTTPError";
import { useSearchParams } from "./useSearchParams";
import { useGetDataFromPermissoToken } from "./useGetDataFromPermissoToken";
import { ExchangeSlugByDocumentationAreaIdAndActorIdResponse } from "src/contexts/documentationArea/domain/DocumentationAreaRepository";
import { DocumentationAreaController } from "src/contexts/documentationArea/application/DocumentationAreaController";

type GetDocumentationAreaIdAndActorIdParams = {
  documentationAreaController: DocumentationAreaController;
  token: string;
  slug: string;
  isMagicLink: boolean;
  isUserLink: boolean;
};

export async function getDocumentationAreaIdAndActorId({
  documentationAreaController,
  token,
  slug,
  isMagicLink,
  isUserLink,
}: GetDocumentationAreaIdAndActorIdParams) {
  if (slug && token) {
    if (isMagicLink) {
      return await documentationAreaController.getDocumentationAreaIdAndActorIdByMagicLinkSlug(
        token,
        slug,
      );
    }
    if (isUserLink) {
      return await documentationAreaController.getDocumentationAreaIdAndActorIdByUserLinkSlug(
        token,
        slug,
      );
    }
  }
  throw new Error("Invalid link type");
}

export function useDocumentationAreaIdAndActorId() {
  const { documentationAreaController } = useDependencies();
  const { token } = useAuth();
  const { slug, isMagicLink, isUserLink, token: searchParamsToken } = useSearchParams();

  if (searchParamsToken) {
    // This shouldnt be here as the responsability of getting the documentationAreaId
    // should be in the queryFn. At this point, its impossible for us to do it properly
    // and we need it here.
    const data = useGetDataFromPermissoToken();
    if (!data) {
      throw new Error("No data from Permisso token");
    }
    return {
      documentationAreaId: data.documentationAreaId,
      actorId: data.actorId,
      isDocumentationAreaIdLoading: false,
      documentationAreaIdError: null,
    };
  }

  const queryFn = () =>
    getDocumentationAreaIdAndActorId({
      documentationAreaController,
      token,
      slug,
      isMagicLink: Boolean(isMagicLink),
      isUserLink: Boolean(isUserLink),
    });

  const { data, isLoading, error } = useQuery<ExchangeSlugByDocumentationAreaIdAndActorIdResponse>({
    queryKey: ["DocumentationAreaId", slug],
    queryFn,
    enabled: Boolean(token && slug && (isMagicLink || isUserLink)),
    retry: false,
    throwOnError: (error) => error instanceof HTTPError && error.getHTTPCode().isNotFound(),
  });

  return {
    isDocumentationAreaIdLoading: isLoading,
    documentationAreaId: data?.documentationAreaId,
    actorId: data?.actorId,
    documentationAreaIdError: error,
  };
}
